import React, { useEffect, useState, useMemo } from "react";
import { api, apiErr } from "@/lib/api";
import PortalLayout from "@/components/PortalLayout";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Loader2, ChevronLeft, ChevronRight, Clock, FolderKanban, Timer,
  Plus, Trash2, CheckCircle2, XCircle, Calendar as CalendarIcon, X, AlertCircle,
  Plane,
} from "lucide-react";

const RO_MONTHS = ["Ianuarie","Februarie","Martie","Aprilie","Mai","Iunie",
  "Iulie","August","Septembrie","Octombrie","Noiembrie","Decembrie"];

// La Enter, transformă descrierea într-o listă cu bullet points.
function bulletKeyDown(ev, onChangeVal) {
  if (ev.key !== "Enter" || ev.shiftKey) return;
  ev.preventDefault();
  const el = ev.target;
  const start = el.selectionStart ?? el.value.length;
  const end = el.selectionEnd ?? el.value.length;
  const val = el.value;
  const before = val.slice(0, start);
  const after = val.slice(end);
  const lineStart = before.lastIndexOf("\n") + 1;
  const currentLine = before.slice(lineStart);
  let newBefore = before;
  // Adaugă bullet și pe linia curentă dacă nu are deja unul
  if (currentLine.trim() && !currentLine.trimStart().startsWith("•")) {
    newBefore = before.slice(0, lineStart) + "• " + before.slice(lineStart);
  }
  const insertion = "\n• ";
  const newVal = newBefore + insertion + after;
  const cursor = (newBefore + insertion).length;
  el.value = newVal;
  el.selectionStart = el.selectionEnd = cursor;
  if (onChangeVal) onChangeVal(newVal);
}
const RO_DAYS = ["Luni","Marți","Miercuri","Joi","Vineri","Sâmbătă","Duminică"];
const RO_DAYS_SHORT = ["Lu","Ma","Mi","Jo","Vi","Sâ","Du"];

// Opțiuni de oră pentru condica de prezență (05:00 → 23:00, pas de 30 min)
const TIME_OPTIONS = (() => {
  const out = [];
  for (let h = 5; h <= 23; h++) {
    for (const mm of ["00", "30"]) {
      out.push(`${String(h).padStart(2, "0")}:${mm}`);
    }
  }
  return out;
})();

function workedHours(start, end) {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  const mins = (eh * 60 + em) - (sh * 60 + sm);
  return mins > 0 ? Math.round((mins / 60) * 100) / 100 : 0;
}

function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function monthKey(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; }

const STATUS_BADGE = {
  submitted: { label: "În așteptare", cls: "bg-amber-100 text-amber-700 border-amber-300", icon: Clock },
  approved: { label: "Aprobat", cls: "bg-emerald-100 text-emerald-700 border-emerald-300", icon: CheckCircle2 },
  rejected: { label: "Respins", cls: "bg-red-100 text-red-700 border-red-300", icon: XCircle },
  pending_deletion: { label: "Ștergere în aprobare", cls: "bg-orange-100 text-orange-700 border-orange-300", icon: Trash2 },
};

export default function Timesheet() {
  const [cursor, setCursor] = useState(() => new Date());
  const [projects, setProjects] = useState([]);
  const [entries, setEntries] = useState([]);
  const [daysOff, setDaysOff] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogDate, setDialogDate] = useState(null); // YYYY-MM-DD

  const y = cursor.getFullYear(), m = cursor.getMonth();
  const monthStr = monthKey(cursor);

  async function load() {
    setLoading(true);
    try {
      const [p, e, off] = await Promise.all([
        api.get("/staff/timesheet/my/projects"),
        api.get("/staff/timesheet/my", { params: { month: monthStr } }),
        api.get("/timeoff/my-days-off", { params: { month: monthStr } }).catch(() => ({ data: [] })),
        api.get("/staff/attendance/my", { params: { month: monthStr } }).then(r => setAttendance(r.data)).catch(() => setAttendance([])),
      ]);
      setProjects(p.data);
      setEntries(e.data);
      setDaysOff(off.data);
    } catch (err) { toast.error(apiErr(err)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [monthStr]);

  // Resincronizare la revenirea în fereastră (ex: admin a modificat alocările)
  useEffect(() => {
    const onFocus = async () => {
      if (document.visibilityState === "hidden") return;
      try {
        const [p, e] = await Promise.all([
          api.get("/staff/timesheet/my/projects"),
          api.get("/staff/timesheet/my", { params: { month: monthStr } }),
        ]);
        setProjects(p.data);
        setEntries(e.data);
      } catch (_) { /* silent */ }
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [monthStr]);

  // Build 6-row × 7-col grid starting Monday
  const grid = useMemo(() => {
    const first = new Date(y, m, 1);
    const startWeekday = (first.getDay() + 6) % 7; // Monday=0
    const start = new Date(y, m, 1 - startWeekday);
    const cells = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      cells.push(d);
    }
    return cells;
  }, [y, m]);

  const entriesByDate = useMemo(() => {
    const map = {};
    for (const e of entries) (map[e.date] = map[e.date] || []).push(e);
    return map;
  }, [entries]);

  const daysOffByDate = useMemo(() => {
    const map = {};
    for (const d of daysOff) map[d.date] = d;
    return map;
  }, [daysOff]);

  const attendanceByDate = useMemo(() => {
    const map = {};
    for (const a of attendance) map[a.date] = a;
    return map;
  }, [attendance]);

  const monthTotals = useMemo(() => {
    const total = entries.reduce((s, e) => s + e.hours, 0);
    const days = new Set(entries.map(e => e.date)).size;
    const coDays = daysOff.filter(d => {
      const t = (d.type_name || "").toLowerCase();
      const c = (d.type_code || "").toLowerCase();
      return c === "annual" || t.includes("odihn");
    }).length;
    return { total, days, coDays };
  }, [entries, daysOff]);

  const projectsById = useMemo(() => {
    const map = {};
    for (const p of projects) map[p.project_id] = p;
    return map;
  }, [projects]);

  const today = ymd(new Date());

  return (
    <PortalLayout variant="staff">
      <div className="max-w-[1400px]">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <div className="section-eyebrow">Pontaj</div>
            <h1 className="font-display font-semibold text-[42px] md:text-[48px] tracking-[-0.02em] leading-tight text-ink">Timesheet-ul meu</h1>
            <p className="mt-3 text-[15px] text-ink-muted">Click pe o zi pentru a adăuga sau edita ore pe proiectele tale.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button data-testid="ts-prev" onClick={() => setCursor(new Date(y, m - 1, 1))}
              className="h-10 w-10 rounded-full border border-slate-200 bg-white/70 backdrop-blur hover:border-indigo-400 hover:bg-indigo-50/60 flex items-center justify-center transition-colors">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="h-10 px-4 md:px-6 rounded-full border border-slate-200 bg-white/70 backdrop-blur flex items-center gap-2 font-display font-semibold text-[14px] min-w-[150px] md:min-w-[200px] justify-center">
              {RO_MONTHS[m]} {y}
            </div>
            <button data-testid="ts-next" onClick={() => setCursor(new Date(y, m + 1, 1))}
              className="h-10 w-10 rounded-full border border-slate-200 bg-white/70 backdrop-blur hover:border-indigo-400 hover:bg-indigo-50/60 flex items-center justify-center transition-colors">
              <ChevronRight className="h-4 w-4" />
            </button>
            <button data-testid="ts-add-today" onClick={() => setDialogDate(today)}
              className="md:ml-4 inline-flex items-center gap-2 bg-ink text-white px-5 py-2.5 rounded-xl font-semibold text-[14px] hover:bg-brand transition-colors shadow-sm">
              <Plus className="h-4 w-4" /> Adaugă pontaj
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8">
          <Stat icon={Clock} label="Total ore" value={`${monthTotals.total.toFixed(1)} h`} accent />
          <Stat icon={FolderKanban} label="Proiecte alocate" value={projects.length} />
          <Stat icon={Timer} label="Zile lucrate" value={monthTotals.days} />
          <Stat icon={Plane} label="Concediu de odihnă" value={monthTotals.coDays} />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-ink-muted">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Se încarcă...
          </div>
        ) : projects.length === 0 ? (
          <div className="surface text-center py-24">
            <FolderKanban className="h-8 w-8 text-ink-muted mx-auto mb-3" />
            <p className="text-ink-muted">Nu ești alocat pe niciun proiect. Contactează administratorul.</p>
          </div>
        ) : (
          <div className="surface overflow-hidden" data-testid="calendar">
            <div className="grid grid-cols-7 border-b border-indigo-100/70 bg-indigo-50/40">
              {RO_DAYS.map((d) => (
                <div key={d} className="px-3 py-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                  <span className="hidden md:inline">{d}</span>
                  <span className="md:hidden">{RO_DAYS_SHORT[RO_DAYS.indexOf(d)]}</span>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {grid.map((d, i) => {
                const inMonth = d.getMonth() === m;
                const dStr = ymd(d);
                const dayEntries = entriesByDate[dStr] || [];
                const dayHours = dayEntries.reduce((s, e) => s + e.hours, 0);
                const isToday = dStr === today;
                const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                const hasRejected = dayEntries.some(e => e.status === "rejected");
                const hasPending = dayEntries.some(e => e.status === "submitted");
                const dayOff = daysOffByDate[dStr];
                const isDayOff = !!dayOff;
                return (
                  <button
                    key={i}
                    data-testid={`day-${dStr}`}
                    onClick={() => { if (!isDayOff) setDialogDate(dStr); }}
                    disabled={!inMonth || isDayOff}
                    title={isDayOff ? (dayOff.is_holiday ? `Sărbătoare legală: ${dayOff.type_name}` : `Concediu aprobat: ${dayOff.type_name}`) : ""}
                    className={`min-h-[64px] md:min-h-[112px] p-1.5 md:p-2.5 border-b border-r border-indigo-50/80 text-left transition-all duration-150 relative
                      ${inMonth && !isDayOff ? "hover:bg-indigo-50/50 hover:shadow-[inset_0_0_0_1px_rgba(111,107,245,0.25)] cursor-pointer" : ""}
                      ${!inMonth ? "bg-slate-50/70 opacity-40 cursor-default" : ""}
                      ${isDayOff && inMonth ? "cursor-not-allowed" : ""}
                      ${isToday && inMonth ? "bg-indigo-50/40" : ""}
                      ${isWeekend && inMonth && !dayOff ? "bg-slate-50/70" : ""}`}
                    style={dayOff && inMonth ? {
                      background: `linear-gradient(135deg, ${dayOff.color}22 0%, ${dayOff.color}0a 100%)`,
                    } : undefined}
                  >
                    <div className="flex items-start justify-between">
                      {isToday && inMonth ? (
                        <span className="h-6 w-6 rounded-full bg-gradient-to-br from-[#3b8ff0] to-[#9b6bf0] text-white flex items-center justify-center text-[12px] font-bold shadow-[0_4px_10px_-3px_rgba(96,110,240,0.6)]">
                          {d.getDate()}
                        </span>
                      ) : (
                        <span className={`font-mono text-sm ${!inMonth ? "text-slate-400" : "text-slate-700"}`}>
                          {d.getDate()}
                        </span>
                      )}
                      {dayHours > 0 && (
                        <span className="inline-flex items-center h-5 px-2 rounded-full text-[11px] font-bold text-white bg-gradient-to-r from-[#3b8ff0] to-[#6f6bf5] shadow-[0_4px_10px_-4px_rgba(96,110,240,0.6)] tabular-nums">
                          {dayHours.toFixed(1)}h
                        </span>
                      )}
                    </div>
                    {dayOff && inMonth && (
                      <div className="mt-2 text-[10px] px-1.5 py-1 rounded-md font-medium truncate"
                        style={{ background: `${dayOff.color}22`, color: dayOff.color, borderLeft: `2px solid ${dayOff.color}` }}
                        title={dayOff.type_name}>
                        {dayOff.type_name}
                      </div>
                    )}
                    <div className="mt-2 space-y-1">
                      {dayEntries.slice(0, 3).map((e) => {
                        const pName = projectsById[e.project_id]?.project?.name || "Proiect";
                        return (
                          <div key={e.id} className={`text-[10px] px-1.5 py-0.5 rounded-md border-l-2 truncate
                            ${e.status === "approved" ? "border-emerald-500 bg-emerald-50 text-emerald-800" :
                              e.status === "rejected" ? "border-red-500 bg-red-50 text-red-800" :
                              "border-amber-500 bg-amber-50 text-amber-900"}`}
                            title={`${pName} · ${e.hours}h ${e.description ? '· '+e.description : ''}`}
                          >
                            <span className="font-semibold">{e.hours}h</span> · {pName}
                          </div>
                        );
                      })}
                      {dayEntries.length > 3 && (
                        <div className="text-[10px] text-ink-muted">+ {dayEntries.length - 3} mai</div>
                      )}
                    </div>
                    {(hasRejected || hasPending) && inMonth && (
                      <div className="absolute top-2 right-2 flex gap-1">
                        {hasRejected && <span className="h-1.5 w-1.5 rounded-full bg-red-500" title="respinse" />}
                        {hasPending && <span className="h-1.5 w-1.5 rounded-full bg-amber-500" title="în așteptare" />}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <DayDialog
        dateStr={dialogDate}
        onClose={() => setDialogDate(null)}
        onChanged={load}
        projects={projects}
        entries={dialogDate ? (entriesByDate[dialogDate] || []) : []}
        projectsById={projectsById}
        attendanceForDay={dialogDate ? (attendanceByDate[dialogDate] || null) : null}
      />
    </PortalLayout>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="surface surface-interactive p-4 md:p-6">
      <div className="flex items-center justify-between mb-3 md:mb-4">
        <div className="text-[12px] md:text-[13px] font-display font-medium text-slate-500">{label}</div>
        <div className={`h-8 w-8 md:h-10 md:w-10 rounded-full flex items-center justify-center ${accent
          ? "bg-gradient-to-br from-[#3b8ff0] to-[#9b6bf0] text-white shadow-[0_8px_18px_-8px_rgba(96,110,240,0.65)]"
          : "bg-indigo-50 text-indigo-400"}`}>
          <Icon className="h-4 w-4 md:h-5 md:w-5" />
        </div>
      </div>
      <div className={`font-display font-semibold text-[28px] md:text-[38px] tracking-[-0.02em] tabular-nums leading-none ${accent ? "text-[#4956e4]" : "text-ink"}`}>{value}</div>
    </div>
  );
}

function DayDialog({ dateStr, onClose, onChanged, projects, entries, projectsById, attendanceForDay }) {
  const [form, setForm] = useState({ project_id: "", hours: "", description: "" });
  const [busy, setBusy] = useState(false);
  const [att, setAtt] = useState({ start_time: "", end_time: "" });
  const [attBusy, setAttBusy] = useState(false);

  // Reset form when dateStr changes
  useEffect(() => {
    setForm({ project_id: "", hours: "", description: "" });
  }, [dateStr]);

  useEffect(() => {
    setAtt({
      start_time: attendanceForDay?.start_time || "09:00",
      end_time: attendanceForDay?.end_time || "18:00",
    });
  }, [dateStr, attendanceForDay]);

  if (!dateStr) return null;

  const d = new Date(dateStr + "T00:00:00");
  const dayLabel = `${RO_DAYS[(d.getDay() + 6) % 7]}, ${d.getDate()} ${RO_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
  const attWorked = workedHours(att.start_time, att.end_time);
  const attendanceFilled = !!(att.start_time && att.end_time && attWorked > 0);

  // Available projects for new entry: exclude those already having an entry that day
  const usedProjectIds = new Set(entries.map(e => e.project_id));
  const availableProjects = projects.filter(p => !usedProjectIds.has(p.project_id));

  async function submit() {
    if (!attendanceFilled) return toast.error("Completează mai întâi condica de prezență (sosire și plecare) pentru această zi.");
    if (!form.project_id) return toast.error("Alege un proiect");
    const hours = parseFloat(form.hours);
    if (isNaN(hours) || hours <= 0 || hours > 24) return toast.error("Introdu un număr de ore între 0 și 24");
    setBusy(true);
    try {
      // Asigură că prezența (ex. valorile prestabilite 09:00–18:00) e salvată înainte de pontaj
      const attPersisted = attendanceForDay?.start_time && attendanceForDay?.end_time;
      if (!attPersisted) {
        await api.post("/staff/attendance", {
          date: dateStr, start_time: att.start_time || null, end_time: att.end_time || null,
        });
      }
      await api.post("/staff/timesheet", {
        project_id: form.project_id, date: dateStr, hours, description: form.description,
      });
      toast.success("Pontaj înregistrat");
      setForm({ project_id: "", hours: "", description: "" });
      await onChanged();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  }

  async function saveAttendance(next) {
    setAttBusy(true);
    try {
      await api.post("/staff/attendance", {
        date: dateStr,
        start_time: next.start_time || null,
        end_time: next.end_time || null,
      });
      await onChanged();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setAttBusy(false); }
  }

  async function updateEntry(entry, patch) {
    setBusy(true);
    try {
      const wasApproved = entry.status === "approved";
      await api.post("/staff/timesheet", {
        project_id: entry.project_id, date: entry.date,
        hours: patch.hours !== undefined ? patch.hours : entry.hours,
        description: patch.description !== undefined ? patch.description : entry.description,
        start_time: patch.start_time !== undefined ? patch.start_time : (entry.start_time || null),
        end_time: patch.end_time !== undefined ? patch.end_time : (entry.end_time || null),
      });
      toast.success(wasApproved
        ? "Modificare trimisă spre re-aprobare managerului"
        : "Pontaj actualizat");
      await onChanged();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  }

  async function deleteEntry(entry) {
    const wasApproved = entry.status === "approved";
    const msg = wasApproved
      ? "Pontajul este aprobat. Ștergerea va fi trimisă spre aprobarea managerului. Continui?"
      : "Sigur ștergi această intrare?";
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/staff/timesheet/${entry.id}`);
      toast.success(data?.pending_deletion
        ? "Cerere de ștergere trimisă spre aprobarea managerului"
        : "Intrare ștearsă");
      await onChanged();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  }

  const totalHours = entries.reduce((s, e) => s + e.hours, 0);

  return (
    <Dialog open={!!dateStr} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[min(960px,95vw)] w-full max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-2xl flex items-center gap-3">
            <span className="h-9 w-9 rounded-full bg-gradient-to-br from-[#3b8ff0] to-[#9b6bf0] text-white flex items-center justify-center shadow-[0_8px_18px_-8px_rgba(96,110,240,0.65)]">
              <CalendarIcon className="h-4 w-4" />
            </span>
            {dayLabel}
          </DialogTitle>
          <DialogDescription>
            {entries.length === 0 ? "Nu ai înregistrat ore în această zi." : `${entries.length} ${entries.length === 1 ? "intrare" : "intrări"} · Total ${totalHours.toFixed(1)} h`}
          </DialogDescription>
        </DialogHeader>

        {/* Existing entries — table style */}
        {entries.length > 0 && (
          <div className="surface-inset mt-2 overflow-x-auto" data-testid="day-entries">
            <table className="pio-table pio-table-static w-full">
              <thead>
                <tr>
                  <th>Proiect</th>
                  <th className="w-[110px]">Ore</th>
                  <th>Descriere</th>
                  <th className="w-[140px]">Status</th>
                  <th className="w-[50px]"></th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => {
                  const pName = projectsById[e.project_id]?.project?.name || "Proiect";
                  const pCode = projectsById[e.project_id]?.project?.code || "";
                  const s = STATUS_BADGE[e.status] || {};
                  const StatusIcon = s.icon;
                  const isApproved = e.status === "approved";
                  const isPendingDel = e.status === "pending_deletion";
                  const locked = isPendingDel; // în așteptarea aprobării ștergerii
                  return (
                    <React.Fragment key={e.id}>
                      <tr className={isApproved ? "bg-emerald-50/40" : ""}>
                        <td>
                          <div className="font-semibold text-[13px] text-slate-800">{pName}</div>
                          <div className="font-mono text-[10px] text-slate-400">{pCode}</div>
                        </td>
                        <td>
                          <input
                            type="number" step="0.5" min="0" max="24"
                            defaultValue={e.hours}
                            disabled={locked}
                            onBlur={(ev) => {
                              const v = parseFloat(ev.target.value);
                              if (!isNaN(v) && v !== e.hours) updateEntry(e, { hours: v });
                            }}
                            className={`pio-input w-20 font-mono ${locked ? "opacity-60" : ""}`}
                            data-testid={`entry-hours-${e.id}`}
                          />
                        </td>
                        <td>
                          <textarea
                            rows={2}
                            placeholder="Descriere activitate..."
                            defaultValue={e.description || ""}
                            disabled={locked}
                            onKeyDown={(ev) => bulletKeyDown(ev)}
                            onBlur={(ev) => { if (ev.target.value !== (e.description || "")) updateEntry(e, { description: ev.target.value }); }}
                            className={`pio-input w-full resize-y leading-snug ${locked ? "opacity-60" : ""}`}
                          />
                        </td>
                        <td>
                          {s.label && (
                            <span className={`pio-badge ${e.status === "approved" ? "pio-badge-green" : e.status === "rejected" ? "pio-badge-red" : "pio-badge-amber"}`}>
                              {StatusIcon && <StatusIcon className="h-3 w-3" />}
                              {s.label}
                            </span>
                          )}
                        </td>
                        <td>
                          {!isPendingDel && (
                            <button
                              data-testid={`entry-delete-${e.id}`}
                              onClick={() => deleteEntry(e)}
                              title={isApproved ? "Cere ștergerea (necesită aprobare)" : "Șterge"}
                              className="h-8 w-8 rounded-full flex items-center justify-center text-red-500 hover:text-red-700 hover:bg-red-50 transition-colors"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                      {isApproved && (
                        <tr>
                          <td colSpan={5} className="!py-1.5">
                            <div className="text-[11px] text-slate-500 px-3">
                              Poți modifica orele/descrierea sau șterge — modificarea va fi retrimisă spre aprobarea managerului.
                            </div>
                          </td>
                        </tr>
                      )}
                      {e.review_comment && (
                        <tr>
                          <td colSpan={5} className="!py-2">
                            <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-xs text-red-800">
                              <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                              <span>Motiv respingere: {e.review_comment}</span>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Add new */}
        {availableProjects.length > 0 ? (
          <div className={`mt-6 p-5 rounded-2xl border transition-colors ${attendanceFilled ? "border-indigo-200/70 bg-indigo-50/40" : "border-slate-200 bg-slate-50/60 opacity-70"}`}>
            <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#4956e4] mb-3 inline-flex items-center gap-1.5">
              <Plus className="h-3.5 w-3.5" /> Adaugă intrare pentru această zi
            </div>
            {!attendanceFilled && (
              <div className="mb-3 flex items-start gap-2 text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="attendance-required-notice">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>Completează mai întâi <b>condica de prezență</b> de mai jos (ora de sosire și de plecare) ca să poți ponta ore pe proiecte.</span>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-[minmax(0,2fr)_90px_minmax(0,2.4fr)_auto] gap-2 items-center">
              <select
                data-testid="new-project"
                value={form.project_id}
                disabled={!attendanceFilled}
                onChange={(e) => setForm({ ...form, project_id: e.target.value })}
                className="pio-input w-full min-w-0 !h-9 disabled:opacity-60"
              >
                <option value="">Alege proiect...</option>
                {availableProjects.map((p) => (
                  <option key={p.project_id} value={p.project_id}>
                    {p.project?.code} — {p.project?.name}
                  </option>
                ))}
              </select>
              <input
                data-testid="new-hours"
                type="number" step="0.5" min="0" max="24"
                placeholder="Ore"
                value={form.hours}
                disabled={!attendanceFilled}
                onChange={(e) => setForm({ ...form, hours: e.target.value })}
                className="pio-input w-full min-w-0 !h-9 font-mono disabled:opacity-60"
              />
              <textarea
                data-testid="new-description"
                rows={2}
                placeholder="Descriere..."
                value={form.description}
                disabled={!attendanceFilled}
                onKeyDown={(ev) => bulletKeyDown(ev, (v) => setForm((f) => ({ ...f, description: v })))}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="pio-input w-full min-w-0 resize-y leading-snug disabled:opacity-60"
              />
              <button
                data-testid="new-save"
                onClick={submit}
                disabled={busy || !attendanceFilled}
                className="pio-btn !h-9 disabled:opacity-60 shrink-0 whitespace-nowrap"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                Salvează
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-6 p-4 rounded-2xl border border-dashed border-indigo-200 text-center text-sm text-slate-500 bg-indigo-50/30">
            Ai deja intrări pentru toate proiectele alocate în această zi.
          </div>
        )}

        {/* Condică de prezență — separată de alocarea pe proiecte */}
        <div className="mt-6 p-5 rounded-2xl border border-slate-200 bg-slate-50/60" data-testid="attendance-section">
          <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 mb-1 inline-flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5" /> Condică de prezență
          </div>
          <p className="text-[12px] text-slate-500 mb-4">Ora la care ai sosit și ai plecat — independent de orele pontate pe proiecte.</p>
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="flex-1 min-w-0">
              <label className="block text-[12px] font-medium text-slate-600 mb-1">Sosire</label>
              <select data-testid="attendance-start" value={att.start_time}
                onChange={(e) => { const next = { ...att, start_time: e.target.value }; setAtt(next); saveAttendance(next); }}
                className="pio-input w-full !h-11 font-mono text-[15px]">
                <option value="">—:—</option>
                {TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="flex-1 min-w-0">
              <label className="block text-[12px] font-medium text-slate-600 mb-1">Plecare</label>
              <select data-testid="attendance-end" value={att.end_time}
                onChange={(e) => { const next = { ...att, end_time: e.target.value }; setAtt(next); saveAttendance(next); }}
                className="pio-input w-full !h-11 font-mono text-[15px]">
                <option value="">—:—</option>
                {TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="sm:w-[150px] shrink-0 rounded-xl bg-white border border-slate-200 px-4 py-2.5 text-center" data-testid="attendance-worked">
              <div className="text-[11px] text-slate-500">Ore lucrate</div>
              <div className="font-display font-semibold text-[26px] leading-none text-[#4956e4] tabular-nums">
                {workedHours(att.start_time, att.end_time).toFixed(1)}
              </div>
              {attBusy && <div className="text-[10px] text-slate-400 mt-1">se salvează…</div>}
            </div>
          </div>
          {att.start_time && att.end_time && workedHours(att.start_time, att.end_time) === 0 && (
            <div className="mt-2 text-[12px] text-amber-600">Ora de plecare trebuie să fie după ora de sosire.</div>
          )}
        </div>

        <div className="mt-4 flex justify-end">
          <button onClick={onClose} className="pio-btn-ghost !h-9 px-4">
            <X className="h-4 w-4" /> Închide
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
